import { message, Tag, Button, Form, Input, InputNumber } from 'antd';
import Card, { CardDelete, CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import dayjs from 'dayjs';
import { capitalize } from 'lodash-es';
import { useState, useEffect } from 'react';
import { providerService } from 'services';
import AsyncSelect from 'components/Select/AsyncSelect';
import { DatePicker } from 'components/ui/DatePicker';
import Switch from 'components/Switch/Switch';
import RadioCards from 'components/RadioCards/RadioCards';
import { Add } from '@carbon/icons-react';
import { ENDASH } from 'constants';
import { DATE_FORMAT_US_SLASH } from 'components/AriaDatePicker/AriaDatePicker';
import { validateDates, getPastDateValidator } from 'utils';
import UploadFile from 'components/UploadFile/UploadFile';
import { PROVIDER_FILE_TYPES } from 'services/providers.service';

const LicenseCard = ({ license, idx, isEditing, form, onDelete }) => {
  const neverExpires = Form.useWatch(`neverExpires_${idx}`, form);

  return (
    <Card
      key={license?.id || `new-${idx}`}
      className="mb-4"
      collapsible
      title={license?.id ? license?.type?.title || ENDASH : 'Unsaved'}
      subtitle={license?.id ? `#${license?.licenseNumber || ''}` : ENDASH}
      extra={isEditing ? <CardDelete onConfirm={() => onDelete(idx)} /> : null}
      noBodyPadding
    >
      <TabularList
        indented
        data={[
          {
            title: 'License type',
            titleRequired: true,
            value: isEditing ? (
              <Form.Item
                name={`type_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'License type is required' }]}
              >
                <AsyncSelect
                  optionsApiUrl="/options/15"
                  getOptionLabel={(option) => option.title}
                  getOptionValue={(option) => option.id}
                  isOptionSelected={(option, [value]) => option?.id === (value?.id || value)}
                />
              </Form.Item>
            ) : (
              license.type?.title
            ),
          },
          {
            title: 'License term',
            value: isEditing ? (
              <Form.Item
                name={`duration_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'License term is required' }]}
              >
                <RadioCards
                  size="small"
                  options={[
                    { value: 'SCHOOL_YEAR_ONLY', label: 'School year only' },
                    { value: 'SUMMER_ONLY', label: 'Summer only' },
                    { value: 'ALL_YEAR', label: 'All year' },
                  ]}
                />
              </Form.Item>
            ) : (
              capitalize((license?.duration?.split('_').join(' ') || '').toLowerCase())
            ),
            titleRequired: true,
          },
          {
            title: 'License number',
            value: isEditing ? (
              <Form.Item
                name={`licenseNumber_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'License number is required' }]}
              >
                <Input placeholder="Enter license number" className="max-w-72" />
              </Form.Item>
            ) : (
              license.licenseNumber
            ),
            titleRequired: true,
          },
          {
            title: 'Start date',
            value: isEditing ? (
              <Form.Item
                name={`startDate_${idx}`}
                className="mb-0"
                rules={[
                  { required: true, message: 'Start date is required' },
                  {
                    validator: validateDates(
                      form.getFieldValue,
                      `startDate_${idx}`,
                      `endDate_${idx}`,
                      'Start date must be before end date',
                    ),
                  },
                ]}
              >
                <DatePicker className="max-w-72" />
              </Form.Item>
            ) : (
              dayjs(license.startDate).format(DATE_FORMAT_US_SLASH)
            ),
            titleRequired: true,
          },
          ...((neverExpires === undefined ? license.neverExpires : neverExpires)
            ? []
            : [
                {
                  title: 'End date',
                  value: isEditing ? (
                    <Form.Item
                      name={`endDate_${idx}`}
                      className="mb-0"
                      rules={[
                        { required: !neverExpires, message: 'End date is required' },
                        {
                          validator: validateDates(
                            form.getFieldValue,
                            `startDate_${idx}`,
                            `endDate_${idx}`,
                            'End date must be after start date',
                          ),
                        },
                      ]}
                    >
                      <DatePicker className="max-w-72" />
                    </Form.Item>
                  ) : (
                    dayjs(license.endDate).format(DATE_FORMAT_US_SLASH)
                  ),
                },
              ]),
          {
            title: 'Expiry',
            value: isEditing ? (
              <Form.Item name={`neverExpires_${idx}`} className="mb-0" valuePropName="checked">
                <Switch checkedChildren="Expires" unCheckedChildren="Never expires" />
              </Form.Item>
            ) : license.neverExpires ? (
              'Does not expire'
            ) : (
              'Expires'
            ),
          },
          {
            title: 'Date of last licensing inspection',
            value: isEditing ? (
              <Form.Item
                name={`lastInspectionDate_${idx}`}
                className="mb-0"
                rules={[
                  { required: true, message: 'Last inspection date is required' },
                  getPastDateValidator('Date of last inspection should be in the past.'),
                ]}
              >
                <DatePicker className="max-w-72" />
              </Form.Item>
            ) : (
              dayjs(license.lastInspectionDate).format(DATE_FORMAT_US_SLASH)
            ),
            titleRequired: true,
          },
          {
            title: 'Capacity',
            value: isEditing ? (
              <Form.Item name={`totalCapacity_${idx}`} className="mb-0">
                <InputNumber min={0} controls={false} placeholder="Enter capacity" className="max-w-72" />
              </Form.Item>
            ) : (
              license.totalCapacity
            ),
          },
        ]}
      />
    </Card>
  );
};

const Licenses = ({ careFacility, application, isEditable = false }) => {
  const [localLicenses, setLocalLicenses] = useState(() => JSON.parse(JSON.stringify(careFacility?.licenses || [])));
  const [deletedLicenses, setDeletedLicenses] = useState([]);
  const [careFacilityFiles, setCareFacilityFiles] = useState([]);
  const [form] = Form.useForm();

  // Fetch care facility files when component mounts or careFacility changes
  useEffect(() => {
    if (careFacility?.id) {
      providerService
        .getCareFacilityFiles(careFacility.id)
        .then((files) => {
          setCareFacilityFiles(files);
        })
        .catch((error) => {
          console.error('Failed to fetch files', error);
        });
    }
  }, [careFacility?.id]);

  const { isEditing, editProps } = useEditCard({
    id: 'licenses',
    onEditStart: () => {
      const formValues = {};
      localLicenses.forEach((license, idx) => {
        formValues[`type_${idx}`] = license.type?.id;
        formValues[`duration_${idx}`] = license.duration;
        formValues[`licenseNumber_${idx}`] = license.licenseNumber;
        formValues[`startDate_${idx}`] = license.startDate ? dayjs(license.startDate).format('YYYY-MM-DD') : '';
        formValues[`endDate_${idx}`] = license.endDate ? dayjs(license.endDate).format('YYYY-MM-DD') : '';
        formValues[`neverExpires_${idx}`] = license.neverExpires;
        formValues[`lastInspectionDate_${idx}`] = license.lastInspectionDate
          ? dayjs(license.lastInspectionDate).format('YYYY-MM-DD')
          : '';
        formValues[`totalCapacity_${idx}`] = license.totalCapacity;
      });
      form.setFieldsValue(formValues);
    },
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const licenses = localLicenses.map((license, idx) => ({
        ...(license.id ? { id: license.id } : {}),
        type: typeof values[`type_${idx}`] === 'number' ? { id: values[`type_${idx}`] } : values[`type_${idx}`],
        duration: values[`duration_${idx}`],
        licenseNumber: values[`licenseNumber_${idx}`],
        startDate: values[`startDate_${idx}`],
        endDate: values[`endDate_${idx}`] || null,
        neverExpires: values[`neverExpires_${idx}`] || false,
        lastInspectionDate: values[`lastInspectionDate_${idx}`],
        totalCapacity: values[`totalCapacity_${idx}`],
        careFacility: { id: careFacility.id },
      }));
      const updatedCenter = await providerService.updateCenter({ licenses }, careFacility.id);
      setLocalLicenses(JSON.parse(JSON.stringify(updatedCenter.licenses || [])));
      setDeletedLicenses([]);
      form.resetFields();
      message.success('Licenses updated');
    },
    onDiscard: () => {
      const savedLicenses = localLicenses.filter((lic) => lic.id !== null);
      setLocalLicenses(JSON.parse(JSON.stringify([...savedLicenses, ...deletedLicenses])));
      setDeletedLicenses([]);
      form.resetFields();
    },
  });

  const handleDeleteLicense = (licenseIndex) => {
    const license = localLicenses[licenseIndex];

    // Add to deleted list if it has an ID (existing license)
    if (license.id) {
      setDeletedLicenses([...deletedLicenses, license]);
    }

    // Remove from local list
    const updatedLicenses = localLicenses.filter((_, idx) => idx !== licenseIndex);
    setLocalLicenses(updatedLicenses);
  };

  const handleAddLicense = () => {
    const newLicense = {
      id: null, // Temporary ID for new licenses
      type: null,
      duration: 'ALL_YEAR',
      licenseNumber: '',
      startDate: new Date().toISOString(),
      endDate: new Date().toISOString(),
      neverExpires: false,
      lastInspectionDate: new Date().toISOString(),
      totalCapacity: null,
    };

    const updatedLicenses = [...localLicenses, newLicense];
    setLocalLicenses(updatedLicenses);

    // Set form values for the new license
    const idx = updatedLicenses.length - 1;
    form.setFieldsValue({
      [`type_${idx}`]: null,
      [`duration_${idx}`]: 'ALL_YEAR',
      [`licenseNumber_${idx}`]: '',
      [`startDate_${idx}`]: dayjs().format('YYYY-MM-DD'),
      [`endDate_${idx}`]: dayjs().format('YYYY-MM-DD'),
      [`neverExpires_${idx}`]: false,
      [`lastInspectionDate_${idx}`]: dayjs().format('YYYY-MM-DD'),
      [`totalCapacity_${idx}`]: null,
    });
  };

  return (
    <Card
      title={
        <div className="flex items-center gap-3">
          <span>Licenses</span>
          <Tag>{localLicenses.length}</Tag>
        </div>
      }
      extra={isEditable ? <CardEdit {...editProps} /> : null}
      stickyHeader
    >
      <Form form={form} disabled={editProps.loading}>
        {localLicenses.length > 0 &&
          localLicenses.map((license, idx) => (
            <LicenseCard
              key={license?.id || `new-${idx}`}
              license={license}
              idx={idx}
              isEditing={isEditing}
              form={form}
              onDelete={handleDeleteLicense}
            />
          ))}

        {/* License Documents Card */}
        <Card title="License documents" className="mt-4" noBodyPadding>
          <TabularList
            indented
            data={[
              {
                title: 'License files',
                titleRequired: true,
                value: (
                  <Form.Item
                    name="licenseFiles"
                    className="mb-0"
                    rules={[
                      () => ({
                        validator() {
                          const licenseFiles = careFacilityFiles?.filter(
                            (file) => file?.fileType === PROVIDER_FILE_TYPES.STATE_LICENSE.code,
                          );
                          if (licenseFiles?.length === 0) {
                            return Promise.reject('At least one license file is required');
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <UploadFile
                      fileCode={PROVIDER_FILE_TYPES.STATE_LICENSE.code}
                      files={careFacilityFiles}
                      setFiles={setCareFacilityFiles}
                      careFacilityId={careFacility?.id}
                      preventDeleteIfLastFile
                      readOnly={!isEditable}
                    />
                  </Form.Item>
                ),
              },
            ]}
          />
        </Card>
      </Form>

      {isEditing && isEditable && (
        <Button
          color="default"
          variant="dashed"
          onClick={handleAddLicense}
          icon={<Add size={20} />}
          block
          className="mt-4"
          disabled={editProps.loading}
        >
          Add license
        </Button>
      )}
    </Card>
  );
};

export { Licenses };
